import { type FC } from "react";
import { Box, Tooltip, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import worldLowRes from "./world-low-res.json";

const Root = styled(Box)(({ theme }) => ({
  "& svg path": {
    ...theme.applyStyles("dark", { fill: "rgba(255,255,255,0.9)" }),
    ...theme.applyStyles("light", { fill: "rgba(0,0,0,0.5)" }),
    cursor: "pointer",
    "&:hover": { fill: "#4caf50", opacity: 1 },
  },
}));

export interface MapChartCountryDatum {
  countryCode: string;
  countryName?: string;
  [field: string]: number | string | undefined;
}

interface MapChartProps {
  data: MapChartCountryDatum[];
  field: string;
}

// Реальная форма world-low-res.json (проверено `head -c` после копирования):
// { id, name, viewBox, layers: [{ id, name, d }] } — плоские SVG-path слои,
// id совпадает с ISO alpha-2 country code (lower-case).
interface WorldLayer {
  id: string;
  name: string;
  d: string;
}

interface WorldMap {
  id: string;
  name: string;
  viewBox: string;
  layers: WorldLayer[];
}

const world = worldLowRes as WorldMap;

export const MapChart: FC<MapChartProps> = ({ data, field }) => {
  const { t } = useTranslation();

  const values = data.map((item) => Number(item[field]) || 0);
  const maxValue = Math.max(...values, 0);
  const byCode = new Map(
    data.map((item) => [item.countryCode.toUpperCase(), item])
  );

  return (
    <Root>
      <svg viewBox={world.viewBox}>
        {world.layers.map((layer) => {
          const country = byCode.get(layer.id.toUpperCase());
          const value = country ? Number(country[field]) || 0 : 0;
          const opacity = value > 0 ? Math.max(value / maxValue, 0.35) : 0.1;

          return (
            <Tooltip
              key={layer.id}
              title={
                <div>
                  <div>
                    <b>{layer.name}</b>
                  </div>
                  {country?.views != null && (
                    <div>
                      {t("shared.mapChart.views", { count: country.views })}
                    </div>
                  )}
                  {country?.visitors != null && (
                    <div>
                      {t("shared.mapChart.visitors", {
                        count: country.visitors,
                      })}
                    </div>
                  )}
                  {country?.sales != null && (
                    <div>
                      {t("shared.mapChart.sales", { count: country.sales })}
                    </div>
                  )}
                  {country?.revenue != null && (
                    <div>
                      {t("shared.mapChart.revenue", { value: country.revenue })}
                    </div>
                  )}
                  {country?.users != null && (
                    <div>
                      {t("shared.mapChart.users", { count: country.users })}
                    </div>
                  )}
                </div>
              }
            >
              <path d={layer.d} style={{ opacity }} />
            </Tooltip>
          );
        })}
      </svg>
    </Root>
  );
};
